import { createClient } from '@supabase/supabase-js';

// Create a singleton instance that persists across HMR
let supabaseInstance = null;
let originalClientInstance = null;

// Default organization ID - will be set after login
let currentOrganizationId = null;

// Function to get a cookie value by name
function getCookie(name) {
  // Check if we're in a browser environment
  if (typeof document === 'undefined') {
    return null;
  }

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Function to set a cookie with a name and value
function setCookie(name, value, days = 7) {
  // Check if we're in a browser environment
  if (typeof document === 'undefined') {
    return;
  }

  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
}

// Create a function to get the Supabase client
export function useSupabase() {
  if (!supabaseInstance) {
    try {
      const runtimeConfig = useRuntimeConfig();

      // Create a new Supabase client
      const newClient = createClient(runtimeConfig.public.SUPABASE_URL, runtimeConfig.public.SUPABASE_KEY);

      // Store the enhanced client
      supabaseInstance = enhanceClient(newClient);

      // Add hot module reload handling if in development
      if (import.meta.hot) {
        import.meta.hot.accept(() => {
          // This callback is triggered when this module is updated
          console.log('[HMR] Updating Supabase client module');
        });

        import.meta.hot.dispose(() => {
          // Cleanup when module is disposed
          console.log('[HMR] Disposing Supabase client module');
          // We keep the instance to prevent connections from being closed
        });
      }
    } catch (error) {
      console.error('Error initializing Supabase client:', error);
      throw error;
    }
  }

  return supabaseInstance;
}

// Function to get the original client
export function getOriginalClient() {
  if (!originalClientInstance) {
    const runtimeConfig = useRuntimeConfig();
    originalClientInstance = createClient(runtimeConfig.public.SUPABASE_URL, runtimeConfig.public.SUPABASE_KEY);
  }
  return originalClientInstance;
}

// Function to set the current organization ID
export function setOrganizationId(orgId) {
  currentOrganizationId = orgId;
  // Store in cookie for persistence across page reloads
  setCookie('currentOrganizationId', orgId);
  console.log('Organization ID set to:', orgId);
}

// Function to get the current organization ID
export function getOrganizationId() {
  // If not set in memory, try to get from cookie
  if (!currentOrganizationId) {
    currentOrganizationId = getCookie('currentOrganizationId');
    console.log('Organization ID retrieved from cookie:', currentOrganizationId);
  }
  return currentOrganizationId;
}

// Function to check if organization ID is set and throw error if not
function validateOrganizationId() {
  const orgId = getOrganizationId();
  if (!orgId) {
    console.warn('No organization ID is set! This will cause RLS policy violations.');
    throw new Error('No organization is selected. Please select an organization first.');
  }
  return orgId;
}

// Tables that should not have organization_id filter automatically applied
const excludedTables = [
  'organizations',
  'auth',
  'storage',
  'documents',
  'pages',
  'locations',
  'point_of_interest',
  'migration_id_mappings',
];

// Function to enhance Supabase client
function enhanceClient(supabase) {
  // Enhanced Supabase client that automatically adds organization_id to inserts
  const enhancedSupabase = {
    // Pass through auth methods directly
    auth: supabase.auth,

    // Add schema method to support custom schema queries
    schema: (schema) => {
      // Get the schema-aware client
      const schemaClient = supabase.schema(schema);

      // Return an enhanced version that maintains our custom functionality
      return {
        ...enhancedSupabase,
        from: (table) => {
          const originalFrom = schemaClient.from(table);

          // Tables in custom schemas should not have organization filters
          const shouldApplyOrgFilters = false;

          return {
            ...originalFrom,
            // Pass through all methods without organization_id filtering
            select: function () {
              return originalFrom.select.apply(originalFrom, arguments);
            },
            insert: function (values, options) {
              return originalFrom.insert(values, options);
            },
            update: function (values, options) {
              return originalFrom.update(values, options);
            },
            delete: function (options) {
              return originalFrom.delete(options);
            },
            upsert: function (values, options) {
              return originalFrom.upsert(values, options);
            },
          };
        },
      };
    },

    // Wrap the from method to enhance table operations
    from: (table) => {
      const originalFrom = supabase.from(table);

      // Check if this is a table with a schema prefix (e.g., 'database.products')
      const hasSchema = table.includes('.');
      // If it has a schema, we should not apply organization filters
      const shouldApplyOrgFilters = !hasSchema && !excludedTables.includes(table);

      return {
        ...originalFrom,

        // Enhance the insert method to include organization_id
        insert: (values, options) => {
          if (!shouldApplyOrgFilters) {
            return originalFrom.insert(values, options);
          }

          const orgId = validateOrganizationId();
          console.log('Inserting with organization ID:', orgId);
          // If values is an array
          if (Array.isArray(values)) {
            // Add organization_id to each object in the array
            const valuesWithOrg = values.map((val) => ({
              ...val,
              organization_id: orgId,
            }));
            return originalFrom.insert(valuesWithOrg, options);
          }
          // If values is a single object
          else if (typeof values === 'object') {
            // Add organization_id to the object
            return originalFrom.insert(
              {
                ...values,
                organization_id: orgId,
              },
              options,
            );
          }
          // Fall back to original behavior
          return originalFrom.insert(values, options);
        },

        // Enhance the select method to include organization_id filter
        select: function () {
          // Skip organization_id filter for excluded tables or tables with schema
          if (!shouldApplyOrgFilters) {
            return originalFrom.select.apply(originalFrom, arguments);
          }

          let orgId;
          try {
            orgId = validateOrganizationId();
            console.log(`[DEBUG] SUPABASE CLIENT - Using organization ID filter for table '${table}': ${orgId}`);
          } catch (err) {
            console.error(`[DEBUG] SUPABASE CLIENT - Error getting organization ID for ${table}:`, err.message);
            throw err; // Re-throw to maintain original behavior
          }

          // Start with the original select
          let selectQuery = originalFrom.select.apply(originalFrom, arguments);
          console.log(`[DEBUG] SUPABASE CLIENT - Original query for ${table} created`);

          // Add organization_id as a filter
          console.log(`[DEBUG] SUPABASE CLIENT - Adding organization_id filter to ${table} query: ${orgId}`);
          selectQuery = selectQuery.eq('organization_id', orgId);

          return selectQuery;
        },

        // Enhance the update method to include organization_id filter
        update: (values, options) => {
          if (!shouldApplyOrgFilters) {
            return originalFrom.update(values, options);
          }

          const orgId = validateOrganizationId();
          console.log('Updating with organization ID filter:', orgId);

          // Start with the original update
          let updateQuery = originalFrom.update(values, options);

          // Add organization_id as a filter
          updateQuery = updateQuery.eq('organization_id', orgId);

          return updateQuery;
        },

        // Enhance the delete method to include organization_id filter
        delete: (options) => {
          if (!shouldApplyOrgFilters) {
            return originalFrom.delete(options);
          }

          const orgId = validateOrganizationId();
          console.log('Deleting with organization ID filter:', orgId);

          // Start with the original delete
          let deleteQuery = originalFrom.delete(options);

          // Add organization_id as a filter
          deleteQuery = deleteQuery.eq('organization_id', orgId);

          return deleteQuery;
        },

        // Enhance the upsert method to include organization_id
        upsert: (values, options) => {
          if (!shouldApplyOrgFilters) {
            return originalFrom.upsert(values, options);
          }

          const orgId = validateOrganizationId();
          console.log('Upserting with organization ID:', orgId);
          // If values is an array
          if (Array.isArray(values)) {
            // Add organization_id to each object in the array
            const valuesWithOrg = values.map((val) => ({
              ...val,
              organization_id: orgId,
            }));
            return originalFrom.upsert(valuesWithOrg, options);
          }
          // If values is a single object
          else if (typeof values === 'object') {
            // Add organization_id to the object
            return originalFrom.upsert(
              {
                ...values,
                organization_id: orgId,
              },
              options,
            );
          }
          // Fall back to original behavior
          return originalFrom.upsert(values, options);
        },
        // Add any other methods you need to pass through
      };
    },

    // Pass through any other methods from the original client
    rpc: supabase.rpc.bind(supabase),
    storage: supabase.storage,
    // Add channel method for realtime subscriptions
    channel: supabase.channel.bind(supabase),
    removeChannel: supabase.removeChannel.bind(supabase),
    // Add any other top-level methods you need
  };

  return enhancedSupabase;
}

// Function to reset the Supabase client instance (useful for testing and debugging)
export function resetSupabaseClient() {
  supabaseInstance = null;
  originalClientInstance = null;
  console.log('Supabase client has been reset');
}

// Debug function to check if organization ID is set
export function checkOrganizationId() {
  const orgId = getOrganizationId();
  console.log('Current Organization ID:', orgId);
  if (!orgId) {
    console.warn('No organization ID is set! This may cause RLS policy violations.');
    return false;
  }
  return true;
}
